import React, { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { DataJoinService } from '../services/dataJoins';
import { CalculationService } from '../services/calculationService';
import { Transaction } from '../types';
import { AnalysisQuery, SavedAnalysis } from '../types/analytics';
import { formatDateForAnalysis, compareFormattedDates } from '../lib/utils';

const COLORS = ['#E8AFB6', '#AFE8C5', '#AFD3E8', '#FFD97D', '#FF6B6B', '#95E1D3'];

const MAX_FILTER_VALUES_IN_BADGE = 4;

function formatCategoricalFilterBadge(
  field: string,
  values: string[],
  fieldLabel?: string
): string {
  if (!values?.length) return fieldLabel || field;
  const label = fieldLabel || field;
  const shown = values.slice(0, MAX_FILTER_VALUES_IN_BADGE);
  const suffix =
    values.length > MAX_FILTER_VALUES_IN_BADGE
      ? ` +${values.length - MAX_FILTER_VALUES_IN_BADGE} more`
      : '';
  return `${label}: ${shown.join(', ')}${suffix}`;
}

interface AnalysisBuilderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (analysis: AnalysisQuery) => void | Promise<void>;
  transactions: Transaction[];
  gradingOrders: any[];
  initialAnalysis?: SavedAnalysis | AnalysisQuery | null;
  mode?: 'create' | 'edit';
  onValidationError?: (message: string) => void;
  /** When true, render as a full-page layout (chart-first) instead of a modal dialog. */
  variant?: 'dialog' | 'page';
  /** Optional cancel handler for page variant (e.g. navigate back). */
  onCancel?: () => void;
}

interface MetricOption {
  value: string;
  label: string;
  type: 'currency' | 'number' | 'percentage';
}

interface DimensionOption {
  value: string;
  label: string;
  type: 'string' | 'number' | 'date';
}

// Helper functions defined outside component to avoid hoisting issues
const parseDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
};

// Use the centralized date formatting function that respects user preferences
const formatDateByGranularity = (date: Date, granularity: string): string => {
  return formatDateForAnalysis(date, granularity);
};

const aggregateMetric = (items: any[], metric: string): number => {
  // Handle count metrics with specific logic
  if (metric === 'count_purchased') {
    return items.length;
  }
  if (metric === 'count_sold') {
    return items.filter(item => item.sale_date && item.sale_date.toString().trim() !== '').length;
  }
  if (metric === 'count_graded') {
    return items.filter(item => item.grading_order_id && item.grading_order_id.toString().trim() !== '').length;
  }
  
  // Handle calculated metrics
  if (metric === 'total_fees') {
    return items.reduce((sum, item) => {
      const marketplaceFees = typeof item.marketplace_fees === 'number' ? item.marketplace_fees : parseFloat(item.marketplace_fees || '0');
      const shippingFees = typeof item.shipping_fees === 'number' ? item.shipping_fees : parseFloat(item.shipping_fees || '0');
      return sum + marketplaceFees + shippingFees;
    }, 0);
  }
  
  if (metric === 'total_gems') {
    const { PreferencesService } = require('../services/preferencesService');
    return items.filter(item => {
      if (!item.grade || !item.grading_order_id) return false;
      
      // Parse grade string (e.g., "PSA 10" -> company: "PSA", grade: "10")
      const companies = ['PSA', 'BGS', 'SGC', 'CSG', 'CGC', 'HGA', 'GMA', 'ISA'];
      let company = '';
      let gradeValue = '';
      
      for (const comp of companies) {
        if (item.grade.toUpperCase().includes(comp)) {
          company = comp;
          const regex = new RegExp(`${comp}\\s*(\\d+(?:\\.\\d+)?)`, 'i');
          const match = item.grade.match(regex);
          if (match) {
            gradeValue = match[1];
          }
          break;
        }
      }
      
      if (!company || !gradeValue) return false;
      return PreferencesService.isGemGrade(company, gradeValue);
    }).length;
  }
  
  if (metric === 'gem_rate') {
    const totalGraded = items.filter(item => item.grading_order_id && item.grading_order_id.toString().trim() !== '').length;
    if (totalGraded === 0) return 0;
    
    const totalGems = aggregateMetric(items, 'total_gems');
    return (totalGems / totalGraded) * 100;
  }

  // Default: sum numeric values
  return items.reduce((sum, item) => {
    const value = item[metric];
    if (typeof value === 'number') return sum + value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value.replace(/[$,]/g, ''));
      return sum + (isNaN(parsed) ? 0 : parsed);
    }
    return sum;
  }, 0);
};

const AnalysisBuilder: React.FC<AnalysisBuilderProps> = ({ 
  open, 
  onOpenChange, 
  onSave, 
  transactions, 
  gradingOrders,
  initialAnalysis = null,
  mode = 'create',
  onValidationError,
  variant = 'dialog',
  onCancel,
}) => {
  const [analysisName, setAnalysisName] = useState('');
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [selectedTrend, setSelectedTrend] = useState<{ field: string; granularity: string } | null>(null);
  const [selectedCategorical, setSelectedCategorical] = useState<string[]>([]);
  const [showNullValues, setShowNullValues] = useState(false);
  const [showFiltersDialog, setShowFiltersDialog] = useState(false);
  const [selectedChartType, setSelectedChartType] = useState<string | null>(null);
  
  // Filters state
  const [categoricalFilters, setCategoricalFilters] = useState<Record<string, string[]>>({});
  const [numericFilters, setNumericFilters] = useState<Record<string, { min?: number; max?: number }>>({});
  const [dateRangeFilters, setDateRangeFilters] = useState<Record<string, { start?: string; end?: string }>>({});
  
  // Temporary filter state for the filter dialog
  const [selectedCategoricalField, setSelectedCategoricalField] = useState<string>('');
  const [tempCategoricalValues, setTempCategoricalValues] = useState<string[]>([]);
  const [selectedNumericField, setSelectedNumericField] = useState<string>('');
  const [tempNumericRange, setTempNumericRange] = useState<{ min?: number; max?: number }>({});
  const [selectedDateField, setSelectedDateField] = useState<string>('');
  const [tempDateRange, setTempDateRange] = useState<{ start?: string; end?: string }>({});

  // Available options
  const availableMetrics: MetricOption[] = useMemo(() => [
    { value: 'cost', label: 'Purchase Cost', type: 'currency' },
    { value: 'sale_price', label: 'Sale Price', type: 'currency' },
    { value: 'calculated_total_cost', label: 'Total Cost', type: 'currency' },
    { value: 'calculated_net_profit', label: 'Net Profit', type: 'currency' },
    { value: 'grading_cost', label: 'Grading Cost', type: 'currency' },
    { value: 'marketplace_fees', label: 'Marketplace Fees', type: 'currency' },
    { value: 'shipping_fees', label: 'Shipping Fees', type: 'currency' },
    { value: 'total_fees', label: 'Total Fees', type: 'currency' },
    { value: 'roi', label: 'ROI', type: 'percentage' },
    { value: 'count_purchased', label: 'Count Purchased', type: 'number' },
    { value: 'count_sold', label: 'Count Sold', type: 'number' },
    { value: 'count_graded', label: 'Count Graded', type: 'number' },
    { value: 'total_gems', label: 'Total Gems', type: 'number' },
    { value: 'gem_rate', label: 'Gem Rate', type: 'percentage' },
  ], []);

  const availableCategoricals: DimensionOption[] = useMemo(() => [
    { value: 'sport', label: 'Sport', type: 'string' },
    { value: 'purchase_platform', label: 'Purchase Platform', type: 'string' },
    { value: 'sale_platform', label: 'Sale Platform', type: 'string' },
    { value: 'manufacturer', label: 'Manufacturer', type: 'string' },
    { value: 'set', label: 'Set', type: 'string' },
    { value: 'card_number', label: 'Card #', type: 'string' },
    { value: 'subject', label: 'Player / Character', type: 'string' },
    { value: 'league', label: 'League', type: 'string' },
    { value: 'insert_name', label: 'Insert / Subset', type: 'string' },
    { value: 'parallel', label: 'Parallel', type: 'string' },
    { value: 'numbering', label: 'Print Run / Numbering', type: 'string' },
    { value: 'grade', label: 'Grade', type: 'string' },
    { value: 'grading_company', label: 'Grading Company', type: 'string' },
    { value: 'grading_service_level', label: 'Service Level', type: 'string' },
  ], []);

  const availableTrends = useMemo(() => [
    { field: 'purchase_date', label: 'Purchase Date', granularities: ['week', 'month', 'quarter', 'year'] },
    { field: 'sale_date', label: 'Sale Date', granularities: ['week', 'month', 'quarter', 'year'] },
    { field: 'grading_submission_date', label: 'Submission Date', granularities: ['week', 'month', 'quarter', 'year'] },
  ], []);

  // Populate form fields when editing an existing analysis
  useEffect(() => {
    if (initialAnalysis && mode === 'edit' && open) {
      setAnalysisName(initialAnalysis.name || '');
      setSelectedMetrics(initialAnalysis.metrics || []);
      
      // Extract trend from groupBy (look for _grouped fields) and timeGranularity
      const groupedDateField = initialAnalysis.groupBy?.find(cat => 
        cat.endsWith('_grouped') && (cat.includes('date') || cat.includes('Date'))
      );
      if (groupedDateField && initialAnalysis.timeGranularity && initialAnalysis.timeGranularity !== 'none') {
        const trendField = groupedDateField.replace('_grouped', '');
        setSelectedTrend({
          field: trendField,
          granularity: initialAnalysis.timeGranularity
        });
      } else {
        setSelectedTrend(null);
      }
      
      // Extract categorical dimensions (exclude time dimensions)
      const timeDimensions = initialAnalysis.groupBy?.filter(cat => 
        cat.endsWith('_grouped') && (cat.includes('date') || cat.includes('Date'))
      ) || [];
      const categoricals = initialAnalysis.groupBy?.filter(cat => !timeDimensions.includes(cat)) || [];
      setSelectedCategorical(categoricals);
      
      // Set chart type if provided
      if (initialAnalysis.chartType) {
        setSelectedChartType(initialAnalysis.chartType);
      }
      
      // Parse filters from saved analysis
      const filters = initialAnalysis.filters || {};
      
      // Extract categorical filters
      const catFilters: Record<string, string[]> = {};
      if (filters.categorical) {
        Object.assign(catFilters, filters.categorical);
      }
      setCategoricalFilters(catFilters);
      
      // Extract numeric filters (check for _min/_max suffix pattern)
      const numFilters: Record<string, { min?: number; max?: number }> = {};
      Object.entries(filters).forEach(([key, value]: [string, any]) => {
        if (key.endsWith('_min')) {
          const field = key.replace(/_min$/, '');
          if (!numFilters[field]) numFilters[field] = {};
          numFilters[field].min = typeof value === 'number' ? value : parseFloat(value);
        } else if (key.endsWith('_max')) {
          const field = key.replace(/_max$/, '');
          if (!numFilters[field]) numFilters[field] = {};
          numFilters[field].max = typeof value === 'number' ? value : parseFloat(value);
        }
      });
      setNumericFilters(numFilters);
      
      // Extract date range filters (check for _start/_end suffix pattern)
      const dateFilters: Record<string, { start?: string; end?: string }> = {};
      Object.entries(filters).forEach(([key, value]: [string, any]) => {
        if (key.endsWith('_start')) {
          const field = key.replace(/_start$/, '');
          if (!dateFilters[field]) dateFilters[field] = {};
          dateFilters[field].start = value;
        } else if (key.endsWith('_end')) {
          const field = key.replace(/_end$/, '');
          if (!dateFilters[field]) dateFilters[field] = {};
          dateFilters[field].end = value;
        }
      });
      setDateRangeFilters(dateFilters);
    } else if (!open || mode === 'create') {
      // Reset form when closing or creating new
      setAnalysisName('');
      setSelectedMetrics([]);
      setSelectedTrend(null);
      setSelectedCategorical([]);
      setCategoricalFilters({});
      setNumericFilters({});
      setDateRangeFilters({});
      setSelectedChartType(null);
    }
  }, [initialAnalysis, mode, open]);

  // Determine valid chart types for current data shape
  const getValidChartTypes = useMemo(() => {
    const metrics = selectedMetrics;
    const categoricals = selectedCategorical;
    const hasTrend = selectedTrend !== null;
    
    const validTypes: string[] = [];
    
    if (metrics.length === 0) return ['blank'];
    if (metrics.length === 1 && !hasTrend && categoricals.length === 0) return ['scalar'];
    if (metrics.length === 1 && hasTrend && categoricals.length === 0) {
      validTypes.push('line', 'bar');
      return validTypes;
    }
    if (metrics.length === 1 && !hasTrend && categoricals.length === 1) {
      validTypes.push('bar', 'pie');
      return validTypes;
    }
    if (metrics.length === 1 && !hasTrend && categoricals.length === 2) {
      validTypes.push('stacked_bar');
      return validTypes;
    }
    if (metrics.length === 1 && hasTrend && categoricals.length > 0) {
      validTypes.push('stacked_bar', 'multiple_line');
      return validTypes;
    }
    if (metrics.length > 1 && hasTrend && categoricals.length === 0) {
      validTypes.push('multiple_line', 'stacked_bar');
      return validTypes;
    }
    if (metrics.length > 1 && !hasTrend && categoricals.length > 0) {
      validTypes.push('bar');
      return validTypes;
    }
    validTypes.push('bar');
    return validTypes;
  }, [selectedMetrics, selectedTrend, selectedCategorical]);

  // Auto-detect chart type based on selections
  const autoChartType = useMemo(() => {
    if (selectedMetrics.length === 0) return 'blank';
    if (selectedMetrics.length === 1 && !selectedTrend && selectedCategorical.length === 0) return 'scalar';
    if (selectedMetrics.length === 1 && selectedTrend && selectedCategorical.length === 0) return 'line';
    if (selectedMetrics.length === 1 && !selectedTrend && selectedCategorical.length === 1) return 'bar';
    if (selectedMetrics.length === 1 && !selectedTrend && selectedCategorical.length === 2) return 'stacked_bar';
    if (selectedMetrics.length === 1 && selectedTrend && selectedCategorical.length > 0) return 'stacked_bar';
    if (selectedMetrics.length > 1 && selectedTrend && selectedCategorical.length === 0) return 'multiple_line';
    if (selectedMetrics.length > 1 && !selectedTrend && selectedCategorical.length > 0) return 'bar';
    return 'bar';
  }, [selectedMetrics, selectedTrend, selectedCategorical]);

  // Effective chart type: use selectedChartType if valid, otherwise use autoChartType
  const effectiveChartType = useMemo(() => {
    if (selectedChartType && getValidChartTypes.includes(selectedChartType)) {
      return selectedChartType;
    }
    return autoChartType;
  }, [selectedChartType, autoChartType, getValidChartTypes]);

  const chartHeightPx = variant === 'page' ? 560 : 300;
  const [pageConfigTab, setPageConfigTab] = useState('metrics');
  const emptyChartMinClass = variant === 'page' ? 'min-h-[18rem] md:min-h-[22rem]' : 'h-64';

  // Get chart type icon
  const getChartTypeIcon = (chartType: string) => {
    switch (chartType) {
      case 'bar': return '📊';
      case 'line': return '📈';
      case 'pie': return '🥧';
      case 'table': return '📋';
      case 'summary': return '📄';
      case 'stacked_bar': return '📊';
      case 'multiple_line': return '📈';
      case 'scalar': return '🔢';
      default: return '📊';
    }
  };

  // Join and enrich data
  const processedData = useMemo(() => {
    if (transactions.length === 0) return [];
    
    // Join transactions with grading orders
    const joined = DataJoinService.joinTransactionsWithGradingOrders(transactions, gradingOrders);
    
    // Enrich with calculated fields
    const enriched = CalculationService.enrichTransactions(joined, gradingOrders);
    
    // Apply filters
    let filtered = enriched;
    
    // Apply categorical filters
    Object.entries(categoricalFilters).forEach(([field, values]) => {
      if (values && values.length > 0) {
        filtered = filtered.filter(item => {
          const value = (item as any)[field];
          return values.includes(value);
        });
      }
    });
    
    // Apply numeric filters
    Object.entries(numericFilters).forEach(([field, range]) => {
      if (range.min !== undefined || range.max !== undefined) {
        filtered = filtered.filter(item => {
          const value = parseFloat((item as any)[field]) || 0;
          if (range.min !== undefined && value < range.min) return false;
          if (range.max !== undefined && value > range.max) return false;
          return true;
        });
      }
    });
    
    // Apply date range filters
    Object.entries(dateRangeFilters).forEach(([field, range]) => {
      if (range.start || range.end) {
        filtered = filtered.filter(item => {
          const dateStr = (item as any)[field];
          if (!dateStr) return false;
          const date = parseDate(dateStr);
          if (!date) return false;
          const start = range.start ? new Date(range.start) : null;
          const end = range.end ? new Date(range.end) : null;
          if (start && date < start) return false;
          if (end && date > end) return false;
          return true;
        });
      }
    });
    
    // Smart filtering based on selected metrics
    // If analyzing sales metrics, exclude items that haven't been sold
    const salesMetrics = ['sale_price', 'calculated_net_profit', 'roi', 'count_sold'];
    const hasSalesMetric = selectedMetrics.some(m => salesMetrics.includes(m));
    if (hasSalesMetric) {
      filtered = filtered.filter(item => item.sale_date && item.sale_date.toString().trim() !== '');
    }
    
    // If analyzing grading metrics, exclude items that haven't been graded
    const gradingMetrics = ['grading_cost', 'total_gems', 'gem_rate', 'count_graded'];
    const hasGradingMetric = selectedMetrics.some(m => gradingMetrics.includes(m));
    if (hasGradingMetric) {
      filtered = filtered.filter(item => item.grading_order_id && item.grading_order_id.toString().trim() !== '');
    }
    
    return filtered;
  }, [transactions, gradingOrders, categoricalFilters, numericFilters, dateRangeFilters, selectedMetrics]);

  // Process data for chart
  const chartData = useMemo(() => {
    if (processedData.length === 0 || selectedMetrics.length === 0) return [];

    // Filter data
    let filtered = processedData;
    if (!showNullValues && selectedCategorical.length > 0) {
      filtered = filtered.filter(item => {
        return selectedCategorical.every(cat => {
          const value = (item as any)[cat];
          return value !== null && value !== undefined && value !== '';
        });
      });
    }

    // Filter out items without the relevant date field when doing time-based analysis
    let dataForChart = filtered;
    if (selectedTrend) {
      dataForChart = dataForChart.filter(item => {
        const dateStr = (item as any)[selectedTrend.field];
        return dateStr && dateStr.toString().trim() !== '';
      });
    }
    
    // Add grouped date fields if trend selected
    const enrichedData = dataForChart.map(item => {
      const result: any = { ...item };
      
      if (selectedTrend) {
        const dateStr = (item as any)[selectedTrend.field];
        if (dateStr) {
          const date = parseDate(dateStr);
          if (date) {
            result[`${selectedTrend.field}_grouped`] = formatDateByGranularity(date, selectedTrend.granularity);
          }
        }
      }
      
      return result;
    });

    // Determine if we need stacked bar chart structure or multiple line chart with categorical
    const isStackedBar = effectiveChartType === 'stacked_bar';
    const isMultipleLine = effectiveChartType === 'multiple_line';
    
    if (isStackedBar || (isMultipleLine && selectedCategorical.length > 0 && selectedTrend)) {
      // For stacked bars, we need different grouping logic
      // X-axis dimension: trend if present, otherwise first categorical
      // Stack dimension: categorical if trend present, otherwise second categorical
      let xAxisDimension: string | null = null;
      let stackDimension: string | null = null;
      
      if (selectedTrend) {
        // Trend + categorical: x-axis is trend, stack is categorical
        xAxisDimension = `${selectedTrend.field}_grouped`;
        stackDimension = selectedCategorical[0] || null;
      } else if (selectedCategorical.length === 2) {
        // Two categoricals: x-axis is first, stack is second
        xAxisDimension = selectedCategorical[0];
        stackDimension = selectedCategorical[1];
      }
      
      if (!xAxisDimension || !stackDimension) {
        return [];
      }
      
      // Group by x-axis dimension, then break down by stack dimension
      const xAxisGroups: Record<string, Record<string, any[]>> = {};
      
      enrichedData.forEach(item => {
        const xValue = (item as any)[xAxisDimension!] || (showNullValues ? 'No Data' : 'Unknown');
        const stackValue = (item as any)[stackDimension!] || (showNullValues ? 'No Data' : 'Unknown');
        
        if (!xAxisGroups[xValue]) {
          xAxisGroups[xValue] = {};
        }
        if (!xAxisGroups[xValue][stackValue]) {
          xAxisGroups[xValue][stackValue] = [];
        }
        xAxisGroups[xValue][stackValue].push(item);
      });
      
      // Get all unique stack values for consistent coloring
      const allStackValues = new Set<string>();
      Object.values(xAxisGroups).forEach(stackGroups => {
        Object.keys(stackGroups).forEach(stackValue => {
          allStackValues.add(stackValue);
        });
      });
      const sortedStackValues = Array.from(allStackValues).sort();
      
      // Create data records for stacked bar chart
      const result: any[] = [];
      Object.entries(xAxisGroups)
        .sort(([a], [b]) => {
          // For date-based charts, ensure chronological sorting using smart date comparison
          if (selectedTrend) {
            return compareFormattedDates(a, b);
          }
          return a.localeCompare(b);
        })
        .forEach(([xValue, stackGroups]) => {
          const record: any = {
            name: xValue,
          };
          
          // For stacked bar: create property for each metric/stack combination
          // For multiple line with categorical: create property for each categorical value (one metric)
          if (isStackedBar) {
            selectedMetrics.forEach(metric => {
              sortedStackValues.forEach(stackValue => {
                const items = stackGroups[stackValue] || [];
                const metricKey = stackValue; // Use stack value as the property key
                record[metricKey] = aggregateMetric(items, metric);
              });
            });
          } else if (isMultipleLine) {
            // Multiple line with categorical: one metric, lines for each categorical value
            const metric = selectedMetrics[0];
            sortedStackValues.forEach(stackValue => {
              const items = stackGroups[stackValue] || [];
              record[stackValue] = aggregateMetric(items, metric);
            });
          }
          
          result.push(record);
        });
      
      return result;
    } else {
      // Regular grouping (non-stacked)
      const groups: Record<string, any[]> = {};
      enrichedData.forEach(item => {
        const keys: string[] = [];
        
        // Add categorical dimensions to keys
        selectedCategorical.forEach(cat => {
          const value = (item as any)[cat];
          keys.push(value || (showNullValues ? 'No Data' : ''));
        });
        
        // Add trend dimension if present
        if (selectedTrend) {
          const groupedField = `${selectedTrend.field}_grouped`;
          const value = (item as any)[groupedField];
          keys.push(value || 'Unknown');
        }
        
        const groupKey = keys.length > 0 ? keys.join('|') : 'All';
        
        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(item);
      });

      // Aggregate by group
      const result: any[] = [];
      Object.entries(groups)
        .sort(([a], [b]) => {
          // For date-based charts, ensure chronological sorting (oldest to newest)
          if (selectedTrend) {
            // Extract the time part from the key (it's the last part after splitting by |)
            const partsA = a.split('|');
            const partsB = b.split('|');
            const timeA = partsA[partsA.length - 1] || a;
            const timeB = partsB[partsB.length - 1] || b;
            // Use smart date comparison that handles all formats
            return compareFormattedDates(timeA, timeB);
          }
          return a.localeCompare(b);
        })
        .forEach(([key, items]) => {
          const groupParts = key.split('|');
          const record: any = {
            name: groupParts[groupParts.length - 1],
          };
          
          // Add categorical dimensions if present
          selectedCategorical.forEach((cat, idx) => {
            if (groupParts[idx]) {
              record[cat] = groupParts[idx];
            }
          });
          
          // Aggregate each metric
          selectedMetrics.forEach(metric => {
            record[metric] = aggregateMetric(items, metric);
          });
          
          result.push(record);
        });

      return result;
    }
  }, [processedData, selectedMetrics, selectedTrend, selectedCategorical, showNullValues, effectiveChartType]);

  const formatValue = (value: number, metric: string): string => {
    const metricOption = availableMetrics.find(m => m.value === metric);
    if (!metricOption) return value.toString();
    
    if (metricOption.type === 'currency') {
      return `$${value.toFixed(2)}`;
    }
    if (metricOption.type === 'percentage') {
      return `${value.toFixed(2)}%`;
    }
    return value.toLocaleString();
  };

  const formatMetricLabel = (metric: string): string => {
    return availableMetrics.find(m => m.value === metric)?.label || metric;
  };

  const handleSave = async () => {
    if (!analysisName.trim() || selectedMetrics.length === 0) {
      const msg = 'Please provide a name and select at least one metric';
      onValidationError?.(msg);
      return;
    }

    const analysis: AnalysisQuery = {
      id: Date.now().toString(),
      name: analysisName,
      dataSource: 'joined_transactions',
      metrics: selectedMetrics,
      groupBy: [],
      chartType: effectiveChartType as any,
      filters: {
        ...categoricalFilters,
        // Serialize numeric filters
        ...Object.entries(numericFilters).reduce((acc, [key, value]) => {
          if (value.min !== undefined) acc[`${key}_min`] = value.min;
          if (value.max !== undefined) acc[`${key}_max`] = value.max;
          return acc;
        }, {} as Record<string, number>),
        // Serialize date range filters
        ...Object.entries(dateRangeFilters).reduce((acc, [key, value]) => {
          if (value.start) acc[`${key}_start`] = value.start;
          if (value.end) acc[`${key}_end`] = value.end;
          return acc;
        }, {} as Record<string, string>),
      },
      timeRange: undefined,
      timeGranularity: undefined,
    };

    // Add groupBy fields
    selectedCategorical.forEach(cat => {
      analysis.groupBy.push(cat);
    });
    if (selectedTrend) {
      analysis.groupBy.push(`${selectedTrend.field}_grouped`);
      analysis.timeGranularity = selectedTrend.granularity as any;
    }

    try {
      await onSave(analysis);
      if (variant === 'dialog') {
        handleClose();
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to save analysis';
      onValidationError?.(msg);
    }
  };

  const handleClose = () => {
    setAnalysisName('');
    setSelectedMetrics([]);
    setSelectedTrend(null);
    setSelectedCategorical([]);
    setShowNullValues(false);
    setCategoricalFilters({});
    setNumericFilters({});
    setDateRangeFilters({});
    onOpenChange(false);
  };

  // Filter management helper functions
  const handleAddCategoricalFilter = () => {
    if (!selectedCategoricalField || tempCategoricalValues.length === 0) return;
    setCategoricalFilters(prev => ({
      ...prev,
      [selectedCategoricalField]: tempCategoricalValues
    }));
    setSelectedCategoricalField('');
    setTempCategoricalValues([]);
  };

  const handleRemoveCategoricalFilter = (field: string) => {
    setCategoricalFilters(prev => {
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };

  const handleAddNumericFilter = () => {
    if (!selectedNumericField || (tempNumericRange.min === undefined && tempNumericRange.max === undefined)) return;
    setNumericFilters(prev => ({
      ...prev,
      [selectedNumericField]: { ...tempNumericRange }
    }));
    setSelectedNumericField('');
    setTempNumericRange({});
  };

  const handleRemoveNumericFilter = (field: string) => {
    setNumericFilters(prev => {
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };

  const handleAddDateFilter = () => {
    if (!selectedDateField || (!tempDateRange.start && !tempDateRange.end)) return;
    setDateRangeFilters(prev => ({
      ...prev,
      [selectedDateField]: { ...tempDateRange }
    }));
    setSelectedDateField('');
    setTempDateRange({});
  };

  const handleRemoveDateFilter = (field: string) => {
    setDateRangeFilters(prev => {
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };

  const handleApplyFilters = () => {
    setShowFiltersDialog(false);
  };

  const handleCancelFilters = () => {
    // Reset temporary state
    setSelectedCategoricalField('');
    setTempCategoricalValues([]);
    setSelectedNumericField('');
    setTempNumericRange({});
    setSelectedDateField('');
    setTempDateRange({});
    setShowFiltersDialog(false);
  };

  // Render chart
  const renderChart = () => {
    if (effectiveChartType === 'blank') {
      return (
        <div className={`flex items-center justify-center border rounded-lg bg-muted/10 ${emptyChartMinClass}`}>
          <div className="text-center space-y-2">
            <div className="text-4xl mb-2">📊</div>
            <p className="text-muted-foreground text-sm">Select metrics and dimensions to visualize your data</p>
          </div>
        </div>
      );
    }

    if (chartData.length === 0) {
      return (
        <div className={`flex items-center justify-center border rounded-lg bg-muted/10 ${emptyChartMinClass}`}>
          <p className="text-muted-foreground">No data available for this analysis</p>
        </div>
      );
    }

    if (effectiveChartType === 'scalar') {
      const value = chartData[0]?.[selectedMetrics[0]] || 0;
      return (
        <div className={`flex items-center justify-center border rounded-lg bg-gradient-primary/10 ${emptyChartMinClass}`}>
          <div className="text-center">
            <p className={`font-bold ${variant === 'page' ? 'text-6xl md:text-7xl' : 'text-5xl'}`}>{formatValue(value, selectedMetrics[0])}</p>
            <p className="text-muted-foreground mt-2">{formatMetricLabel(selectedMetrics[0])}</p>
          </div>
        </div>
      );
    }

    const chartHeight = chartHeightPx;

    if (effectiveChartType === 'line' || effectiveChartType === 'multiple_line') {
      // For multiple_line with categorical breakdown, get line keys from chart data (categorical values)
      // For regular line or multiple_line with multiple metrics, use metrics
      let lineKeys: string[] = [];
      let isCategoricalLines = false;

      if (effectiveChartType === 'multiple_line' && chartData.length > 0) {
        // Check if chart data has categorical keys (like stacked_bar structure)
        const firstRow = chartData[0] as any;
        const categoricalKeys = Object.keys(firstRow).filter(key => {
          if (key === 'name') return false;
          // Check if this key corresponds to a categorical dimension value
          // (not a metric name that matches our metric list)
          return !selectedMetrics.includes(key);
        });

        // If we have categorical keys and only one metric, use categorical as lines
        if (categoricalKeys.length > 0 && selectedMetrics.length === 1) {
          lineKeys = categoricalKeys;
          isCategoricalLines = true;
        } else {
          // Fallback: use metrics as lines (multiple metrics, no categorical)
          lineKeys = selectedMetrics;
        }
      } else {
        // Regular line chart: use metrics
        lineKeys = selectedMetrics;
      }

      return (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
            <XAxis 
              dataKey="name" 
              stroke="hsl(var(--muted-foreground))"
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis width={72} tickFormatter={(v) => formatValue(v, selectedMetrics[0])} stroke="hsl(var(--muted-foreground))" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--popover))', 
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px'
              }}
              formatter={(value: number, name: string) => {
                // For categorical lines, use the first metric for formatting
                // For metric lines, find the matching metric
                const metric = isCategoricalLines 
                  ? selectedMetrics[0]
                  : selectedMetrics.find(m => formatMetricLabel(m) === name) || selectedMetrics[0];
                return formatValue(value, metric);
              }}
              labelFormatter={(label) => label}
            />
            <Legend />
            {lineKeys.map((key, idx) => (
              <Line 
                key={key}
                type="monotone" 
                dataKey={key} 
                stroke={COLORS[idx % COLORS.length]} 
                name={isCategoricalLines ? key : formatMetricLabel(key)}
                strokeWidth={2}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (effectiveChartType === 'pie') {
      // Pie charts need special data format
      const pieData = chartData.map((item: any) => ({
        name: item.name,
        value: item[selectedMetrics[0]] || 0
      }));

      return (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }: { name: string; percent: number }) => `${name}: ${(percent * 100).toFixed(1)}%`}
              outerRadius={chartHeight * 0.35}
              fill="#8884d8"
              dataKey="value"
            >
              {pieData.map((entry: any, index: number) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--popover))', 
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px'
              }}
              formatter={(value: number) => {
                return formatValue(value, selectedMetrics[0]);
              }}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    if (effectiveChartType === 'stacked_bar') {
      return (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
            <XAxis 
              dataKey="name" 
              stroke="hsl(var(--muted-foreground))"
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis width={72} tickFormatter={(v) => formatValue(v, selectedMetrics[0])} stroke="hsl(var(--muted-foreground))" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--popover))', 
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px'
              }}
              formatter={(value: number, name: string) => {
                // Stacked bar charts use one metric, format based on that metric
                const metric = selectedMetrics[0];
                return formatValue(value, metric);
              }}
              labelFormatter={(label) => label}
            />
            <Legend />
            {chartData[0] && Object.keys(chartData[0])
              .filter(key => {
                // Filter out 'name' (x-axis label) and categorical dimension field names
                if (key === 'name') return false;
                // Filter out any keys that match categorical dimension names (these are field names, not stack values)
                if (selectedCategorical.includes(key)) return false;
                // Filter out any keys that match metric names (if multiple metrics, we only want stack values)
                // For stacked bars, we expect stack values like "NBA", "MLB" etc. as the keys
                return true;
              })
              .map((key, idx) => (
                <Bar 
                  key={key}
                  dataKey={key} 
                  stackId="stack"
                  fill={COLORS[idx % COLORS.length]}
                  name={key}
                />
              ))}
          </BarChart>
        </ResponsiveContainer>
      );
    }

    // Default bar chart
    return (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
          <XAxis 
            dataKey="name" 
            stroke="hsl(var(--muted-foreground))"
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis width={72} tickFormatter={(v) => formatValue(v, selectedMetrics[0])} stroke="hsl(var(--muted-foreground))" />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: 'hsl(var(--popover))', 
              border: '1px solid hsl(var(--border))',
              borderRadius: '6px'
            }}
            formatter={(value: number, name: string) => {
              // Find the metric by matching the formatted label
              const metric = selectedMetrics.find(m => formatMetricLabel(m) === name) || selectedMetrics[0];
              return formatValue(value, metric);
            }}
            labelFormatter={(label) => label}
          />
          <Legend />
          {selectedMetrics.map((metric, idx) => (
            <Bar 
              key={metric}
              dataKey={metric} 
              fill={COLORS[idx % COLORS.length]}
              name={formatMetricLabel(metric)}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderConfigurationForm = () => (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="analysis-name">Analysis Name *</Label>
              <Input
                id="analysis-name"
                value={analysisName}
                onChange={(e) => setAnalysisName(e.target.value)}
                placeholder="e.g., Sales by Month"
              />
            </div>

            <Separator />

            <div className="space-y-3">
              <Label>Metrics *</Label>
              {(() => {
                // Determine if we're in single metric mode (only when 2 combined trend/breakouts are selected)
                // This happens when: (1 metric + trend + categorical) OR (1 metric + 2 categoricals)
                const isSingleMetricMode = selectedMetrics.length === 1 && 
                  ((selectedTrend !== null && selectedCategorical.length > 0) || selectedCategorical.length >= 2);
                
                return (
                  <>
                    {isSingleMetricMode && (
                      <p className="text-xs text-muted-foreground">
                        With multiple trend/breakouts selected: Only one metric allowed. Select a different metric to switch.
                      </p>
                    )}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      {availableMetrics.map(metric => {
                        const isChecked = selectedMetrics.includes(metric.value);
                        
                        if (isSingleMetricMode) {
                          // Radio button mode: allow switching but not adding
                          return (
                            <div key={metric.value} className="flex items-center space-x-2">
                              <input
                                type="radio"
                                checked={isChecked}
                                onChange={() => {
                                  // Replace the current metric with the selected one
                                  setSelectedMetrics([metric.value]);
                                }}
                                className="w-4 h-4"
                              />
                              <Label className="text-sm font-normal">{metric.label}</Label>
                            </div>
                          );
                        } else {
                          // Checkbox mode: allow multiple selections
                          return (
                            <div key={metric.value} className="flex items-center space-x-2">
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedMetrics([...selectedMetrics, metric.value]);
                                  } else {
                                    setSelectedMetrics(selectedMetrics.filter(m => m !== metric.value));
                                  }
                                }}
                              />
                              <Label className="text-sm font-normal">{metric.label}</Label>
                            </div>
                          );
                        }
                      })}
                    </div>
                  </>
                );
              })()}
            </div>

            <Separator />

            <div className="space-y-3">
              <Label>Trend (Time Period)</Label>
              {selectedMetrics.length > 1 && (
                <p className="text-xs text-muted-foreground">
                  Multiple metrics selected: Only one trend allowed
                </p>
              )}
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {availableTrends.map(trend => (
                  <div key={trend.field} className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        checked={selectedTrend?.field === trend.field}
                        onChange={() => {
                          if (!selectedTrend || selectedTrend.field !== trend.field) {
                            setSelectedTrend({ field: trend.field, granularity: 'month' });
                            // If multiple metrics selected, clear categorical selections (mutual exclusivity)
                            if (selectedMetrics.length > 1) {
                              setSelectedCategorical([]);
                            }
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <Label className="text-sm font-normal">{trend.label}</Label>
                    </div>
                    {selectedTrend?.field === trend.field && (
                      <div className="ml-6 space-y-2">
                        {trend.granularities.map(granularity => (
                          <div key={granularity} className="flex items-center space-x-2">
                            <input
                              type="radio"
                              checked={selectedTrend.granularity === granularity}
                              onChange={() => setSelectedTrend({ field: trend.field, granularity })}
                              className="w-4 h-4"
                            />
                            <Label className="text-sm font-normal capitalize">{granularity}</Label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    checked={selectedTrend === null}
                    onChange={() => {
                      setSelectedTrend(null);
                      // Note: Don't clear categorical here - user might want to select categorical after clearing trend
                    }}
                    className="w-4 h-4"
                  />
                  <Label className="text-sm font-normal">None</Label>
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Filters</Label>
                <Button variant="outline" size="sm" onClick={() => setShowFiltersDialog(true)}>
                  Configure Filters
                </Button>
              </div>
              {(Object.keys(categoricalFilters).length > 0 || Object.keys(numericFilters).length > 0 || Object.keys(dateRangeFilters).length > 0) && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(categoricalFilters).map(([field, values]) => (
                    values && values.length > 0 && (
                      <Badge key={field} variant="secondary" className="text-xs max-w-full truncate">
                        {formatCategoricalFilterBadge(
                          field,
                          values,
                          availableCategoricals.find((c) => c.value === field)?.label
                        )}
                      </Badge>
                    )
                  ))}
                  {Object.entries(numericFilters).map(([field, range]) => (
                    (range.min !== undefined || range.max !== undefined) && (
                      <Badge key={field} variant="secondary" className="text-xs">
                        {availableMetrics.find(m => m.value === field)?.label || field}: {range.min !== undefined ? `≥${range.min}` : ''} {range.max !== undefined ? `≤${range.max}` : ''}
                      </Badge>
                    )
                  ))}
                  {Object.entries(dateRangeFilters).map(([field, range]) => (
                    (range.start || range.end) && (
                      <Badge key={field} variant="secondary" className="text-xs">
                        {availableTrends.find(t => t.field === field)?.label || field}: {range.start || '...'} to {range.end || '...'}
                      </Badge>
                    )
                  ))}
                </div>
              )}
            </div>

            <Separator />

            <div className="space-y-3">
              <Label>Categorical Breakdown</Label>
              {selectedMetrics.length > 1 && (
                <p className="text-xs text-muted-foreground">
                  Multiple metrics selected: Only one categorical allowed
                </p>
              )}
              {selectedMetrics.length === 1 && selectedTrend && selectedCategorical.length >= 1 && (
                <p className="text-xs text-muted-foreground">
                  With trend selected: Only one categorical allowed
                </p>
              )}
              {selectedMetrics.length === 1 && !selectedTrend && (
                <p className="text-xs text-muted-foreground">
                  Up to 2 categorical breakdowns allowed (creates stacked bar chart)
                </p>
              )}
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {availableCategoricals.map(cat => {
                  const isChecked = selectedCategorical.includes(cat.value);
                  const maxReached = (selectedMetrics.length > 1 || selectedTrend) 
                    ? selectedCategorical.length >= 1 
                    : selectedCategorical.length >= 2;
                  const canSelect = isChecked || !maxReached;
                  
                  return (
                    <div key={cat.value} className="flex items-center space-x-2">
                      <Checkbox
                        checked={isChecked}
                        disabled={!canSelect}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedCategorical([...selectedCategorical, cat.value]);
                            // If multiple metrics selected, clear trend selection (mutual exclusivity)
                            if (selectedMetrics.length > 1 && selectedTrend !== null) {
                              setSelectedTrend(null);
                            }
                          } else {
                            setSelectedCategorical(selectedCategorical.filter(c => c !== cat.value));
                          }
                        }}
                      />
                      <Label className="text-sm font-normal">{cat.label}</Label>
                    </div>
                  );
                })}
              </div>
              {selectedCategorical.length > 0 && (
                <div className="flex items-center space-x-2 mt-2">
                  <Checkbox
                    checked={showNullValues}
                    onCheckedChange={setShowNullValues as any}
                  />
                  <Label className="text-sm">Include null/empty values</Label>
                </div>
              )}
            </div>
          </div>
  );

  const renderMetricsPanelContent = () => (
    <div className="space-y-3">
      <div className="space-y-3">
      <Label>Metrics *</Label>
      {(() => {
      // Determine if we're in single metric mode (only when 2 combined trend/breakouts are selected)
      // This happens when: (1 metric + trend + categorical) OR (1 metric + 2 categoricals)
      const isSingleMetricMode = selectedMetrics.length === 1 &&
      ((selectedTrend !== null && selectedCategorical.length > 0) || selectedCategorical.length >= 2);
      return (
      <>
      {isSingleMetricMode && (
      <p className="text-xs text-muted-foreground">
      With multiple trend/breakouts selected: Only one metric allowed. Select a different metric to switch.
      </p>
      )}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
      {availableMetrics.map(metric => {
      const isChecked = selectedMetrics.includes(metric.value);
      if (isSingleMetricMode) {
      // Radio button mode: allow switching but not adding
      return (
      <div key={metric.value} className="flex items-center space-x-2">
      <input
      type="radio"
      checked={isChecked}
      onChange={() => {
      // Replace the current metric with the selected one
      setSelectedMetrics([metric.value]);
      }}
      className="w-4 h-4"
      />
      <Label className="text-sm font-normal">{metric.label}</Label>
      </div>
      );
      } else {
      // Checkbox mode: allow multiple selections
      return (
      <div key={metric.value} className="flex items-center space-x-2">
      <Checkbox
      checked={isChecked}
      onCheckedChange={(checked) => {
      if (checked) {
      setSelectedMetrics([...selectedMetrics, metric.value]);
      } else {
      setSelectedMetrics(selectedMetrics.filter(m => m !== metric.value));
      }
      }}
      />
      <Label className="text-sm font-normal">{metric.label}</Label>
      </div>
      );
      }
      })}
      </div>
      </>
      );
      })()}
      </div>
    </div>
  );

  const renderTimePanelContent = () => (
    <div className="space-y-3">
      <div className="space-y-3">
      <Label>Trend (Time Period)</Label>
      {selectedMetrics.length > 1 && (
      <p className="text-xs text-muted-foreground">
      Multiple metrics selected: Only one trend allowed
      </p>
      )}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
      {availableTrends.map(trend => (
      <div key={trend.field} className="space-y-2">
      <div className="flex items-center space-x-2">
      <input
      type="radio"
      checked={selectedTrend?.field === trend.field}
      onChange={() => {
      if (!selectedTrend || selectedTrend.field !== trend.field) {
      setSelectedTrend({ field: trend.field, granularity: 'month' });
      // If multiple metrics selected, clear categorical selections (mutual exclusivity)
      if (selectedMetrics.length > 1) {
      setSelectedCategorical([]);
      }
      }
      }}
      className="w-4 h-4"
      />
      <Label className="text-sm font-normal">{trend.label}</Label>
      </div>
      {selectedTrend?.field === trend.field && (
      <div className="ml-6 space-y-2">
      {trend.granularities.map(granularity => (
      <div key={granularity} className="flex items-center space-x-2">
      <input
      type="radio"
      checked={selectedTrend.granularity === granularity}
      onChange={() => setSelectedTrend({ field: trend.field, granularity })}
      className="w-4 h-4"
      />
      <Label className="text-sm font-normal capitalize">{granularity}</Label>
      </div>
      ))}
      </div>
      )}
      </div>
      ))}
      <div className="flex items-center space-x-2">
      <input
      type="radio"
      checked={selectedTrend === null}
      onChange={() => {
      setSelectedTrend(null);
      // Note: Don't clear categorical here - user might want to select categorical after clearing trend
      }}
      className="w-4 h-4"
      />
      <Label className="text-sm font-normal">None</Label>
      </div>
      </div>
      </div>
    </div>
  );

  const renderFiltersPanelContent = () => (
    <div className="space-y-3">
      <div className="space-y-3">
      <div className="flex items-center justify-between">
      <Label>Filters</Label>
      <Button variant="outline" size="sm" onClick={() => setShowFiltersDialog(true)}>
      Configure Filters
      </Button>
      </div>
      {(Object.keys(categoricalFilters).length > 0 || Object.keys(numericFilters).length > 0 || Object.keys(dateRangeFilters).length > 0) && (
      <div className="flex flex-wrap gap-2">
      {Object.entries(categoricalFilters).map(([field, values]) => (
      values && values.length > 0 && (
      <Badge key={field} variant="secondary" className="text-xs">
      {formatCategoricalFilterBadge(
        field,
        values,
        availableCategoricals.find((c) => c.value === field)?.label
      )}
      </Badge>
      )
      ))}
      {Object.entries(numericFilters).map(([field, range]) => (
      (range.min !== undefined || range.max !== undefined) && (
      <Badge key={field} variant="secondary" className="text-xs">
      {availableMetrics.find(m => m.value === field)?.label || field}: {range.min !== undefined ? `≥${range.min}` : ''} {range.max !== undefined ? `≤${range.max}` : ''}
      </Badge>
      )
      ))}
      {Object.entries(dateRangeFilters).map(([field, range]) => (
      (range.start || range.end) && (
      <Badge key={field} variant="secondary" className="text-xs">
      {availableTrends.find(t => t.field === field)?.label || field}: {range.start || '...'} to {range.end || '...'}
      </Badge>
      )
      ))}
      </div>
      )}
      </div>
    </div>
  );

  const renderBreakdownPanelContent = () => (
    <div className="space-y-3">
      <div className="space-y-3">
      <Label>Categorical Breakdown</Label>
      {selectedMetrics.length > 1 && (
      <p className="text-xs text-muted-foreground">
      Multiple metrics selected: Only one categorical allowed
      </p>
      )}
      {selectedMetrics.length === 1 && selectedTrend && selectedCategorical.length >= 1 && (
      <p className="text-xs text-muted-foreground">
      With trend selected: Only one categorical allowed
      </p>
      )}
      {selectedMetrics.length === 1 && !selectedTrend && (
      <p className="text-xs text-muted-foreground">
      Up to 2 categorical breakdowns allowed (creates stacked bar chart)
      </p>
      )}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
      {availableCategoricals.map(cat => {
      const isChecked = selectedCategorical.includes(cat.value);
      const maxReached = (selectedMetrics.length > 1 || selectedTrend)
      ? selectedCategorical.length >= 1
      : selectedCategorical.length >= 2;
      const canSelect = isChecked || !maxReached;
      return (
      <div key={cat.value} className="flex items-center space-x-2">
      <Checkbox
      checked={isChecked}
      disabled={!canSelect}
      onCheckedChange={(checked) => {
      if (checked) {
      setSelectedCategorical([...selectedCategorical, cat.value]);
      // If multiple metrics selected, clear trend selection (mutual exclusivity)
      if (selectedMetrics.length > 1 && selectedTrend !== null) {
      setSelectedTrend(null);
      }
      } else {
      setSelectedCategorical(selectedCategorical.filter(c => c !== cat.value));
      }
      }}
      />
      <Label className="text-sm font-normal">{cat.label}</Label>
      </div>
      );
      })}
      </div>
      {selectedCategorical.length > 0 && (
      <div className="flex items-center space-x-2 mt-2">
      <Checkbox
      checked={showNullValues}
      onCheckedChange={setShowNullValues as any}
      />
      <Label className="text-sm">Include null/empty values</Label>
      </div>
      )}
      </div>
    </div>
  );

  const renderStylePanelContent = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Chart type</Label>
        <Select value={effectiveChartType} onValueChange={(v) => setSelectedChartType(v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {getValidChartTypes.map((chartType) => (
              <SelectItem key={chartType} value={chartType}>
                {getChartTypeIcon(chartType)} {chartType.replace('_', ' ').toUpperCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
  const renderChartPanel = () => (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label className="text-base font-medium">Chart</Label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-border bg-transparent hover:bg-muted/50 cursor-pointer"
            >
              {getChartTypeIcon(effectiveChartType)} {effectiveChartType.replace('_', ' ').toUpperCase()}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[200px]">
            <DropdownMenuLabel>Change Chart Type</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {getValidChartTypes.map((chartType) => (
              <DropdownMenuItem
                key={chartType}
                onClick={() => {
                  if (chartType !== effectiveChartType) {
                    setSelectedChartType(chartType);
                  }
                }}
                className={chartType === effectiveChartType ? 'bg-muted' : ''}
              >
                <span className="mr-2">{getChartTypeIcon(chartType)}</span>
                {chartType === effectiveChartType && '✓ '}
                {chartType.replace('_', ' ').toUpperCase()}
                {chartType === effectiveChartType && ' (current)'}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="rounded-lg border bg-card p-2 md:p-4 min-h-[280px]">
        {renderChart()}
      </div>
    </div>
  );

  const handlePageCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      handleClose();
    }
  };

  const builderBody =
    variant === 'page' ? (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {mode === 'edit' ? 'Edit analysis' : 'Create analysis'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Tune your analysis on the right — the chart updates as you change it.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handlePageCancel}>
              Cancel
            </Button>
            <Button variant="gradient" onClick={handleSave}>
              Save analysis
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_22rem] gap-6 lg:gap-8 items-start">
          <Card className="border-2 shadow-sm lg:sticky lg:top-24">
            <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
              <CardTitle className="text-lg">Live preview</CardTitle>
              <Button variant="ghost" size="sm" className="text-xs shrink-0" onClick={() => setPageConfigTab('style')}>
                Change
              </Button>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border bg-card p-2 md:p-4 min-h-[280px]">{renderChart()}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="analysis-name-page">Analysis name *</Label>
                <Input
                  id="analysis-name-page"
                  value={analysisName}
                  onChange={(e) => setAnalysisName(e.target.value)}
                  placeholder="e.g., Sales by Month"
                />
              </div>
              <Tabs value={pageConfigTab} onValueChange={setPageConfigTab} className="w-full">
                <TabsList className="grid w-full grid-cols-5 gap-1.5 p-1 h-auto rounded-md bg-muted">
                  <TabsTrigger
                    value="metrics"
                    className="h-9 w-full rounded-md text-[11px] font-medium leading-none shadow-none data-[state=active]:shadow-none data-[state=active]:bg-background px-1"
                  >
                    Metrics
                  </TabsTrigger>
                  <TabsTrigger
                    value="time"
                    className="h-9 w-full rounded-md text-[11px] font-medium leading-none shadow-none data-[state=active]:shadow-none data-[state=active]:bg-background px-1"
                  >
                    Time
                  </TabsTrigger>
                  <TabsTrigger
                    value="filters"
                    className="h-9 w-full rounded-md text-[11px] font-medium leading-none shadow-none data-[state=active]:shadow-none data-[state=active]:bg-background px-1"
                  >
                    Filters
                  </TabsTrigger>
                  <TabsTrigger
                    value="breakdown"
                    className="h-9 w-full rounded-md text-[11px] font-medium leading-none shadow-none data-[state=active]:shadow-none data-[state=active]:bg-background px-1"
                  >
                    Breakdown
                  </TabsTrigger>
                  <TabsTrigger
                    value="style"
                    className="h-9 w-full rounded-md text-[11px] font-medium leading-none shadow-none data-[state=active]:shadow-none data-[state=active]:bg-background px-1"
                  >
                    Style
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="metrics" className="mt-4 max-h-[calc(100vh-14rem)] overflow-y-auto pr-1">
                  {renderMetricsPanelContent()}
                </TabsContent>
                <TabsContent value="time" className="mt-4 max-h-[calc(100vh-14rem)] overflow-y-auto pr-1">
                  {renderTimePanelContent()}
                </TabsContent>
                <TabsContent value="filters" className="mt-4 max-h-[calc(100vh-14rem)] overflow-y-auto pr-1">
                  {renderFiltersPanelContent()}
                </TabsContent>
                <TabsContent value="breakdown" className="mt-4 max-h-[calc(100vh-14rem)] overflow-y-auto pr-1">
                  {renderBreakdownPanelContent()}
                </TabsContent>
                <TabsContent value="style" className="mt-4 max-h-[calc(100vh-14rem)] overflow-y-auto pr-1">
                  {renderStylePanelContent()}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    ) : (
      <>
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Edit Analysis' : 'Create Analysis'}</DialogTitle>
          <DialogDescription>
            Build custom analytics from your transaction and grading data
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4">
          <div className="order-2 space-y-6 lg:order-1">{renderConfigurationForm()}</div>
          <div className="order-1 lg:order-2">{renderChartPanel()}</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="gradient" onClick={handleSave}>
            Save Analysis
          </Button>
        </DialogFooter>
      </>
    );

  return (
    <>
      {variant === 'page' ? (
        open ? <div className="space-y-6">{builderBody}</div> : null
      ) : (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">{builderBody}</DialogContent>
        </Dialog>
      )}

      {/* Filters Dialog */}
      <Dialog open={showFiltersDialog} onOpenChange={setShowFiltersDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configure Filters</DialogTitle>
            <DialogDescription>
              Select fields to filter your data by categories, numbers, and dates
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Categorical Filters */}
            <div className="space-y-3">
              <h3 className="font-semibold">Categorical Filters</h3>
              
              {/* Active categorical filters */}
              {Object.keys(categoricalFilters).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.entries(categoricalFilters).map(([field, values]) => (
                    values && values.length > 0 && (
                      <Badge key={field} variant="secondary" className="text-xs flex items-center gap-1">
                        {formatCategoricalFilterBadge(
        field,
        values,
        availableCategoricals.find((c) => c.value === field)?.label
      )}
                        <button
                          onClick={() => handleRemoveCategoricalFilter(field)}
                          className="ml-1 hover:text-destructive"
                        >
                          ×
                        </button>
                      </Badge>
                    )
                  ))}
                </div>
              )}
              
              {/* Add new categorical filter */}
              <div className="space-y-3 border rounded-lg p-4">
                <div className="space-y-2">
                  <Label>Select Field</Label>
                  <Select value={selectedCategoricalField} onValueChange={setSelectedCategoricalField}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a categorical field..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCategoricals.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedCategoricalField && (
                  <>
                    <div className="space-y-2">
                      <Label>Select Values</Label>
                      <div className="max-h-48 overflow-y-auto space-y-2 border rounded-md p-3">
                        {(() => {
                          const uniqueValues = Array.from(
                            new Set(
                              processedData
                                .map((item: any) => item[selectedCategoricalField])
                                .filter(v => v !== null && v !== undefined && v !== '')
                            )
                          ).sort();
                          
                          return uniqueValues.map(value => (
                            <div key={value} className="flex items-center space-x-2">
                              <Checkbox
                                checked={tempCategoricalValues.includes(value)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setTempCategoricalValues([...tempCategoricalValues, value]);
                                  } else {
                                    setTempCategoricalValues(tempCategoricalValues.filter(v => v !== value));
                                  }
                                }}
                              />
                              <Label className="text-sm font-normal">{value}</Label>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddCategoricalFilter}
                      disabled={tempCategoricalValues.length === 0}
                    >
                      Add Filter
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Numeric Filters */}
            <div className="space-y-3">
              <h3 className="font-semibold">Numeric Filters</h3>
              
              {/* Active numeric filters */}
              {Object.keys(numericFilters).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.entries(numericFilters).map(([field, range]) => (
                    (range.min !== undefined || range.max !== undefined) && (
                      <Badge key={field} variant="secondary" className="text-xs flex items-center gap-1">
                        {availableMetrics.find(m => m.value === field)?.label || field}: {range.min !== undefined ? `≥${range.min}` : ''} {range.max !== undefined ? `≤${range.max}` : ''}
                        <button
                          onClick={() => handleRemoveNumericFilter(field)}
                          className="ml-1 hover:text-destructive"
                        >
                          ×
                        </button>
                      </Badge>
                    )
                  ))}
                </div>
              )}
              
              {/* Add new numeric filter */}
              <div className="space-y-3 border rounded-lg p-4">
                <div className="space-y-2">
                  <Label>Select Field</Label>
                  <Select value={selectedNumericField} onValueChange={setSelectedNumericField}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a numeric field..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableMetrics.filter(m => m.type === 'currency' || m.type === 'number').map(metric => (
                        <SelectItem key={metric.value} value={metric.value}>
                          {metric.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedNumericField && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-2">
                        <Label htmlFor={`${selectedNumericField}-min`} className="text-xs">Min</Label>
                        <Input
                          id={`${selectedNumericField}-min`}
                          type="number"
                          value={tempNumericRange.min !== undefined ? tempNumericRange.min : ''}
                          onChange={(e) => {
                            setTempNumericRange(prev => ({
                              ...prev,
                              min: e.target.value ? parseFloat(e.target.value) : undefined
                            }));
                          }}
                          placeholder="Min"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`${selectedNumericField}-max`} className="text-xs">Max</Label>
                        <Input
                          id={`${selectedNumericField}-max`}
                          type="number"
                          value={tempNumericRange.max !== undefined ? tempNumericRange.max : ''}
                          onChange={(e) => {
                            setTempNumericRange(prev => ({
                              ...prev,
                              max: e.target.value ? parseFloat(e.target.value) : undefined
                            }));
                          }}
                          placeholder="Max"
                        />
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddNumericFilter}
                      disabled={tempNumericRange.min === undefined && tempNumericRange.max === undefined}
                    >
                      Add Filter
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Date Range Filters */}
            <div className="space-y-3">
              <h3 className="font-semibold">Date Range Filters</h3>
              
              {/* Active date filters */}
              {Object.keys(dateRangeFilters).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.entries(dateRangeFilters).map(([field, range]) => (
                    (range.start || range.end) && (
                      <Badge key={field} variant="secondary" className="text-xs flex items-center gap-1">
                        {availableTrends.find(t => t.field === field)?.label || field}: {range.start || '...'} to {range.end || '...'}
                        <button
                          onClick={() => handleRemoveDateFilter(field)}
                          className="ml-1 hover:text-destructive"
                        >
                          ×
                        </button>
                      </Badge>
                    )
                  ))}
                </div>
              )}
              
              {/* Add new date filter */}
              <div className="space-y-3 border rounded-lg p-4">
                <div className="space-y-2">
                  <Label>Select Field</Label>
                  <Select value={selectedDateField} onValueChange={setSelectedDateField}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a date field..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTrends.map(trend => (
                        <SelectItem key={trend.field} value={trend.field}>
                          {trend.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedDateField && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-2">
                        <Label htmlFor={`${selectedDateField}-start`} className="text-xs">Start Date</Label>
                        <Input
                          id={`${selectedDateField}-start`}
                          type="date"
                          value={tempDateRange.start || ''}
                          onChange={(e) => {
                            setTempDateRange(prev => ({
                              ...prev,
                              start: e.target.value
                            }));
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`${selectedDateField}-end`} className="text-xs">End Date</Label>
                        <Input
                          id={`${selectedDateField}-end`}
                          type="date"
                          value={tempDateRange.end || ''}
                          onChange={(e) => {
                            setTempDateRange(prev => ({
                              ...prev,
                              end: e.target.value
                            }));
                          }}
                        />
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddDateFilter}
                      disabled={!tempDateRange.start && !tempDateRange.end}
                    >
                      Add Filter
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancelFilters}>Cancel</Button>
            <Button variant="gradient" onClick={handleApplyFilters}>Apply Filters</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AnalysisBuilder;
